import {computed, ref} from 'vue'
import {endOfYear, format, isSameDay, isSameYear, isValid, parseISO, startOfYear} from 'date-fns'
import {nb} from 'date-fns/locale'
import {range as _range} from 'lodash'
import {DateRange, DateRangeCoordinates} from './DateRange'

export default (defaultStart: Date, defaultEnd: Date) => {
    const startDate = ref<Date>(new Date(Date.UTC(defaultStart.getFullYear(), defaultStart.getMonth(), defaultStart.getDate())))
    const endDate = ref<Date>(new Date(Date.UTC(defaultEnd.getFullYear(), defaultEnd.getMonth(), defaultEnd.getDate())))
    const todayDate = ref<Date>(new Date())

    const range = computed({
        get: (): DateRange => ({start: startDate.value, end: endDate.value}),
        set: (range: DateRange) => {
            startDate.value = range.start
            endDate.value = range.end
        }
    })


    const formattedStart = computed({
        get() {
            return format(startDate.value, 'yyyy-MM-dd')
        },
        set(value: string) {
            const date = new Date(value)
            if (isValid(date)) {
                startDate.value = parseISO(value)
            }
        }
    })

    const formattedEnd = computed({
        get() {
            return format(endDate.value, 'yyyy-MM-dd')
        },
        set(value: string) {
            const date = new Date(value)
            if (isValid(date)) {
                endDate.value = parseISO(value)
            }
        }
    })

    const isTodaySelected = computed(() => {
        return isSameDay(startDate.value, todayDate.value) && isSameDay(endDate.value, todayDate.value)
    })

    const selectToday = () => {
        startDate.value = todayDate.value
        endDate.value = todayDate.value
    }

    // YTD LOGIC
    const selectYtd = () => {
        startDate.value = startOfYear(new Date())
        endDate.value = new Date()
    }

    const isYtdSelected = computed(() => {
        const start = startDate.value
        const end = endDate.value
        return isSameYear(start, end) && isSameDay(startOfYear(start), start) && isSameDay(end, new Date())
    })

    // YEAR LOGIC
    const startYear = computed(() => {
        return startDate.value.getFullYear()
    })

    const todayYear = computed(() => {
        return todayDate.value.getFullYear()
    })

    const isYearSelected = computed(() => {
        const start = startDate.value
        const end = endDate.value

        return isSameYear(start, end) && isSameDay(startOfYear(start), start) && isSameDay(endOfYear(end), end)
    })

    const rangeIsWithinYear = (year: number) => {
        const start = startDate.value
        const end = endDate.value
        return start.getFullYear() === year && end.getFullYear() === year
    }

    const selectedYear = () => {
        if (isYearSelected.value) return startDate.value.getFullYear()
        return
    }

    const selectYear = (year: number) => {
        startDate.value = startOfYear(new Date(Date.UTC(year, 0, 1)))
        endDate.value = endOfYear(new Date(Date.UTC(year, 11, 31)))
    }

    const selectPrevYear = () => {
        selectYear(startYear.value - 1)
    }

    const selectNextYear = () => {
        selectYear(startYear.value + 1)
    }

    // QAURTER LOGIC
    const isQuarterSelected = (quarter: number) => {
        const start = startDate.value
        const end = endDate.value

        if (start.getFullYear() !== end.getFullYear()) return false

        const year = startYear.value

        const startMonth = (quarter - 1) * 3
        const endMonth = startMonth + 3

        const quarterStart = new Date(Date.UTC(year, startMonth, 1))
        const quarterEnd = new Date(Date.UTC(year, endMonth, 0))

        return start <= quarterStart && quarterEnd <= end
    }

    const quarters = computed(() => {
        return [1, 2, 3, 4].map(q => {
            return {
                quarter: q,
                selected: isQuarterSelected(q)
            }
        })
    })

    const selectQuarter = (quarter: number) => {
        const startMonth = (quarter - 1) * 3
        const endMonth = startMonth + 3
        startDate.value = new Date(Date.UTC(startYear.value, startMonth, 1))
        endDate.value = new Date(Date.UTC(startYear.value, endMonth, 0))
    }

    const selectedQuarter = computed(() => {
        return quarters.value.find(q => q.selected)?.quarter
    })

    const selectPreviousQuarter = () => {
        const currentQuarter = selectedQuarter.value
        if (!currentQuarter) return
        if (currentQuarter === 1) {
            startDate.value = startOfYear(new Date(Date.UTC(startYear.value - 1, 0, 1)))
            selectQuarter(4)
        } else {
            selectQuarter(currentQuarter - 1)
        }
    }

    const selectNextQuarter = () => {
        const currentQuarter = selectedQuarter.value
        if (!currentQuarter) return
        if (currentQuarter === 4) {
            startDate.value = startOfYear(new Date(Date.UTC(startYear.value + 1, 0, 1)))
            selectQuarter(1)
        } else {
            selectQuarter(currentQuarter + 1)
        }
    }

    const selectQuarters = (quarters: { startQuarter: number, endQuarter: number }) => {
        const effectiveStartQuarter = Math.min(quarters.startQuarter, quarters.endQuarter)
        const effectiveEndQuarter = Math.max(quarters.startQuarter, quarters.endQuarter)

        const startMonth = (effectiveStartQuarter - 1) * 3
        const endMonth = effectiveEndQuarter * 3

        startDate.value = new Date(Date.UTC(startYear.value, startMonth, 1))
        endDate.value = new Date(Date.UTC(startYear.value, endMonth, 0))
    }


    //TERM LOGIC
    const isTermSelected = (term: number) => {
        const start = startDate.value
        const end = endDate.value

        if (start.getFullYear() !== end.getFullYear()) return false

        const year = startYear.value

        const startMonth = (term - 1) * 2
        const endMonth = term * 2

        const termStart = new Date(Date.UTC(year, startMonth, 1))
        const termEnd = new Date(Date.UTC(year, endMonth, 0))

        return start <= termStart && termEnd <= end
    }

    const terms = computed(() => {
        return _range(1, 7).map(t => {
            return {
                term: t,
                selected: isTermSelected(t)
            }
        })
    })

    const selectedTerm = computed(() => {
        return terms.value.find(t => t.selected)?.term
    })

    const selectPrevTerm = () => {
        const currentTerm = selectedTerm.value
        if (!currentTerm) return
        if (currentTerm === 1) {
            selectTermAndYear(6, startYear.value - 1)
        } else {
            selectTermAndYear(currentTerm - 1, startYear.value)
        }
    }

    const selectNextTerm = () => {
        const currentTerm = selectedTerm.value
        if (!currentTerm) return
        if (currentTerm === 6) {
            selectTermAndYear(1, startYear.value + 1)
        } else {
            selectTermAndYear(currentTerm + 1, startYear.value)
        }
    }

    const selectTerm = (term: number) => {
        const startMonth = (term - 1) * 2
        const endMonth = startMonth + 2
        startDate.value = new Date(Date.UTC(startYear.value, startMonth, 1))
        endDate.value = new Date(Date.UTC(startYear.value, endMonth, 0))
    }

    const selectTermAndYear = (term: number, year: number) => {
        if (term === -1) {
            selectYear(year)
            return
        }
        const startMonth = (term - 1) * 2
        const endMonth = startMonth + 2
        startDate.value = new Date(Date.UTC(year, startMonth, 1))
        endDate.value = new Date(Date.UTC(year, endMonth, 0))
    }

    const selectTerms = (terms: { startTerm: number, endTerm: number }) => {
        const effectiveStartTerm = Math.min(terms.startTerm, terms.endTerm)
        const effectiveEndTerm = Math.max(terms.startTerm, terms.endTerm)

        const startMonth = (effectiveStartTerm - 1) * 2
        const endMonth = effectiveEndTerm * 2

        startDate.value = new Date(Date.UTC(startYear.value, startMonth, 1))
        endDate.value = new Date(Date.UTC(startYear.value, endMonth, 0))
    }


    // MONTH LOGIC
    const months = computed(() => {
        return _range(0, 12).map(m => {
            return {
                month: m,
                selected: isMonthSelected(m),
                name: format(new Date(Date.UTC(startYear.value, m, 1)), 'MMM', {locale: nb})
            }
        })
    })

    const isMonthSelected = (month: number) => {
        const start = startDate.value
        const end = endDate.value

        if (start.getFullYear() !== end.getFullYear()) return false

        const year = startYear.value

        const startMonth = new Date(Date.UTC(year, month, 1))
        const endMonth = new Date(Date.UTC(year, month + 1, 0))

        return start <= startMonth && endMonth <= end
    }

    const selectMonth = (month: number) => {
        startDate.value = new Date(Date.UTC(startYear.value, month, 1))
        endDate.value = new Date(Date.UTC(startYear.value, month + 1, 0))
    }

    const selectMonths = (months: { startMonth: number, endMonth: number }) => {
        const effectiveStartMonth = Math.min(months.startMonth, months.endMonth)
        const effectiveEndMonth = Math.max(months.startMonth, months.endMonth)

        startDate.value = new Date(Date.UTC(startYear.value, effectiveStartMonth, 1))
        endDate.value = new Date(Date.UTC(startYear.value, effectiveEndMonth + 1, 0))
    }

    const selectMonthAndYear = (month: number, year: number) => {
        startDate.value = new Date(Date.UTC(year, month, 1))
        endDate.value = new Date(Date.UTC(year, month + 1, 0))
    }

    const selectedMonth = computed(() => {
        return months.value.find(m => m.selected)
    })

    const selectPrevMonth = () => {
        if (!selectedMonth.value) return
        const currentMonth = selectedMonth.value.month
        if (currentMonth === 0) {
            selectMonthAndYear(11, startYear.value - 1)
        } else {
            selectMonth(currentMonth - 1)
        }
    }

    const selectNextMonth = () => {
        if (!selectedMonth.value) return
        const currentMonth = selectedMonth.value.month
        if (currentMonth === 11) {
            selectMonthAndYear(0, startYear.value + 1)
        } else {
            selectMonth(currentMonth + 1)
        }
    }


    const selectPreviousPeriod = (type: DateRangeCoordinates['row'] | undefined) => {
        switch (type) {
            case 'YEAR':
                selectPrevYear()
                break
            case 'QUARTER':
                selectPreviousQuarter()
                break
            case 'TERM':
                selectPrevTerm()
                break
            case 'MONTH':
                selectPrevMonth()
                break
            default:
                return
        }
    }

    const selectNextPeriod = (type: DateRangeCoordinates['row'] | undefined) => {
        switch (type) {
            case 'YEAR':
                selectNextYear()
                break
            case 'QUARTER':
                selectNextQuarter()
                break
            case 'TERM':
                selectNextTerm()
                break
            case 'MONTH':
                selectNextMonth()
                break
        }
    }


    return {
        startDate,
        endDate,
        range, rangeIsWithinYear,
        formattedStart,
        formattedEnd,
        selectToday, isTodaySelected, selectYtd, isYtdSelected,
        todayYear, startYear, selectYear, selectNextYear, selectPrevYear, isYearSelected, selectedYear,
        quarters, selectQuarter, selectQuarters,
        terms, selectTerm, selectTerms, selectTermAndYear,
        months, selectMonth, selectMonths,
        selectPreviousPeriod, selectNextPeriod
    }
}